import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";

import {
  ForecastSlot,
  IntervalPrice,
  useGetPriceForecast,
  useGetPrices,
} from "api";
import { Widget } from "components/elements/mui";
import { useFormatMessage, useLocaleContext } from "lang";
import { PRICES } from "routes/consts";
import { getSelectedPowerPlant, useAppSelector } from "store";
import { pulseSx, sourceFlashSx } from "styles/animations";

import {
  Source,
  currentSlotEntry,
  formatTimeAgo,
  getCurrentSlot,
  latestEntry,
  slotPeriod,
} from "./utils";

const PriceColumnSkeleton = () => (
  <Stack alignItems="center" gap={0.75} flex={1} minWidth={0}>
    <Skeleton variant="text" width={28} animation="wave" />
    <Skeleton
      variant="rounded"
      width={72}
      height={28}
      animation="wave"
      sx={{ borderRadius: 1 }}
    />
    <Skeleton variant="text" width={80} animation="wave" />
  </Stack>
);

const PriceColumn = ({
  label,
  entry,
  source,
  updatedAt,
  flashId,
  forecast,
}: {
  label: string;
  entry: IntervalPrice | null;
  source: Source;
  // When the source last published the data now on screen, plus a token that
  // changes whenever the user asks for a refresh. Undefined flashId leaves the
  // column exactly as it was (the dashboard doesn't show the timestamp).
  updatedAt?: string | null;
  flashId?: number;
  // The nowcast for the slot in progress, shown under the last published
  // price. `null` reserves the space without content so the other column
  // stays aligned; undefined renders nothing (RDN has no nowcast).
  forecast?: ForecastSlot | null;
}) => {
  const formatMessage = useFormatMessage();
  const { dateFnsLocale } = useLocaleContext();
  const value = entry?.[source] ?? null;
  const isNegative = value !== null && value < 0;

  return (
    <Stack alignItems="center" gap={0.5} flex={1} minWidth={0}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        letterSpacing={0.5}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="baseline" gap={0.5}>
        <Typography
          variant="h4"
          fontWeight={700}
          color={isNegative ? "error.main" : "text.primary"}
          lineHeight={1}
        >
          {value !== null ? Math.round(value) : "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          PLN/MWh
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 11 }}
      >
        {entry ? slotPeriod(entry.time) : " "}
      </Typography>
      {forecast !== undefined && (
        // Mirrors the measured block above — value, then its period — at a
        // smaller size and pulsing in step with the rest of the page. Fixed
        // height so the column never resizes.
        <Stack alignItems="center" gap={0.25} sx={{ height: 34, ...pulseSx() }}>
          {forecast && (
            <>
              <Stack direction="row" alignItems="baseline" gap={0.5}>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  lineHeight={1}
                  color={forecast.p50 < 0 ? "error.main" : "text.primary"}
                >
                  {Math.round(forecast.p50)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PLN/MWh
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11, lineHeight: 1.2 }}
              >
                {slotPeriod(forecast.time)}
              </Typography>
            </>
          )}
        </Stack>
      )}
      {flashId !== undefined && (
        // Fixed height, and the caption's line height is pinned to match, so
        // the reveal occupies space that was already there.
        <Box
          sx={{
            height: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {flashId > 0 && updatedAt && (
            <Typography
              key={flashId}
              variant="caption"
              color="text.secondary"
              role="status"
              sx={{ ...sourceFlashSx, fontSize: 11, lineHeight: "14px" }}
            >
              {formatMessage("prices.updatedAt", {
                values: {
                  // Read once: the reveal is on screen for a few seconds, so
                  // there is nothing for a ticking clock to correct.
                  time: formatTimeAgo(updatedAt, new Date(), dateFnsLocale),
                },
              })}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
};

export const CurrentPricesWidget = ({
  disableLink = false,
  flashId,
}: {
  disableLink?: boolean;
  // Passed by the prices page: a counter bumped when a refresh the user asked
  // for lands, which reveals each source's timestamp under its own price.
  flashId?: number;
}) => {
  const formatMessage = useFormatMessage();
  const { data, isLoading } = useGetPrices();
  const { data: forecast } = useGetPriceForecast();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const timezone = selectedPowerPlant?.timezone || "Europe/Warsaw";

  // PSE forecast lags 15–30 min, so show the latest published slot.
  // RDN is published for the whole day, so show the current slot directly.
  const latestPse = latestEntry(data?.today, "pse");
  const currentRdn = currentSlotEntry(data?.today, timezone);
  // The nowcast for the slot in progress — what the preliminary is expected
  // to say once PSE publishes it in ~27 minutes. The slot has begun, so this
  // is the forecast that stood when it began (the same one the prices page
  // and the switch-off simulation show), not a later run's revision.
  const currentSlot = getCurrentSlot(timezone);
  const nowcast =
    forecast?.cen?.history.find((s) => s.time === currentSlot) ??
    forecast?.cen?.slots.find((s) => s.time === currentSlot) ??
    null;

  return (
    <Widget
      size={{ xs: 12 }}
      title={formatMessage("prices.latestPrices")}
      url={disableLink ? undefined : PRICES}
      content={
        isLoading ? (
          <Box py={0.5}>
            <Stack
              direction="row"
              divider={<Divider orientation="vertical" flexItem />}
            >
              <PriceColumnSkeleton />
              <PriceColumnSkeleton />
            </Stack>
          </Box>
        ) : (
          <Box py={0.5}>
            <Stack
              direction="row"
              divider={<Divider orientation="vertical" flexItem />}
            >
              <PriceColumn
                label={formatMessage("prices.pse")}
                entry={latestPse}
                source="pse"
                updatedAt={data?.today.sources.pse}
                flashId={flashId}
                forecast={nowcast}
              />
              <PriceColumn
                label={formatMessage("prices.rdn")}
                entry={currentRdn}
                source="rdn"
                updatedAt={data?.today.sources.rdn}
                flashId={flashId}
                forecast={nowcast ? null : undefined}
              />
            </Stack>
          </Box>
        )
      }
    />
  );
};
